import type { ArchivedResearchReference } from './workspace/archivedResearch';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthSession as User } from './auth';
import { authFetch, currentUser, isDelegated } from './auth';
import { FutureOptionalityPanel } from './FutureOptionalityPanel';
import { ResearchDraftReview } from './workspace/ResearchDraftReview';
import { readCalculatorDefaults, type ResearchScope, type ResearchValues } from './workspace/researchDraft';
import {
  calculateDomain,
  fallbackDomainInput,
  getDomainDefaults,
  DomainInput,
  DomainOutput,
  MixedUseViewResult,
  PvPhase,
  RenovationPhase,
  RentalViewResult,
  ViewResult,
  WealthDeltaContext
} from './domainCalculator';

type Field = {
  key: NumericDomainInputKey;
  label: string;
  suffix?: string;
  prefix?: string;
  min?: number;
  step?: number;
};

type NumericDomainInputKey = {
  [Key in keyof DomainInput]-?: DomainInput[Key] extends number ? Key : never;
}[keyof DomainInput];

type ObjectMatrixEntry = { id: string; name: string; input: DomainInput };

const propertySpecificKeys: Array<keyof DomainInput> = [
  'purchasePrice', 'purchaseCostRate', 'targetRentZm', 'ownerCostsMonthly', 'recurringCapexMonthly',
  'rentalColdRentMonthly', 'rentalNonRecoverableCostsMonthly', 'rentalMaintenanceMonthly',
  'rentalVacancyRate', 'rentalIncomeTaxRate', 'allowNegativeRentalTaxOffset', 'rentalBuildingValueShare',
  'rentalDepreciationRate', 'selfUseAreaM2', 'rentalAreaM2', 'netElwCashflowMonthly', 'elwColdRentMonthly',
  'elwVacancyRate', 'elwNonRecoverableCostsMonthly', 'elwMaintenanceMonthly', 'elwTaxRate',
  'elwLegallyRentable', 'elwLegalRentabilityHaircut', 'separateEntrance', 'legalLivingArea', 'roomHeightOk',
  'rescueRouteOk', 'separateMetersOrAllocation', 'ownParking', 'landValueShare', 'buildingValueShare',
  'rentalBuildingShare', 'rentalDebtInterestAllocationMethod', 'rentalDebtInterestShare', 'rentalAllocatedLoanAmount',
  'livingAreaM2', 'energyCertificateAreaM2', 'currentHeatingCostMonthly', 'currentCoolingKwhPerActiveMonth',
  'currentCoolingActiveMonths', 'candidateEnergyDemandKwhM2a', 'heatDemandKwhM2a', 'heatPumpJaz',
  'auxiliaryElectricityKwhYear', 'candidateEnergyPriceEurKwh', 'candidateEnergySource', 'candidateCoolingProfile',
  'electricityPriceEurKwh', 'pvBenefitMonthly', 'pvPhases', 'energyCostGrowthRate', 'co2PriceEurTon', 'co2PriceGrowthRate', 'immediateCapex',
  'futureCapex', 'futureCapexYear', 'futureCapexDurationYears', 'capexValueEffectRate', 'renovationPhases',
  'poolAnnualCost', 'poolFutureCapex', 'poolFutureCapexYear', 'saunaFutureCapex', 'saunaFutureCapexYear',
  'pvBatteryReplacementYear', 'pvBatteryReplacementCost', 'inverterReplacementYear', 'inverterReplacementCost',
  'heatPumpReplacementYear', 'heatPumpReplacementCost', 'ventilationReplacementYear', 'ventilationReplacementCost',
  'kitchenFurnitureDepreciationMonthly', 'sellingCostRate', 'mixedUseSelfUseYears'
];

function matrixInput(householdInput: DomainInput, objectInput: DomainInput): DomainInput {
  const merged = { ...householdInput } as DomainInput;
  for (const key of propertySpecificKeys) {
    (merged as Record<string, unknown>)[key] = objectInput[key];
  }
  merged.renovationPhases = objectInput.renovationPhases.map((phase) => ({ ...phase }));
  merged.pvPhases = objectInput.pvPhases.map((phase) => ({ ...phase }));
  return merged;
}

const purchaseFields: Field[] = [
  { key: 'purchasePrice', label: 'Offer price', prefix: '€', min: 0, step: 1000 },
  { key: 'purchaseCostRate', label: 'Purchase costs', suffix: '%', min: 0, step: 0.01 },
  { key: 'immediateCapex', label: 'Immediate capex', prefix: '€', min: 0, step: 1000 },
  { key: 'futureCapex', label: 'Future capex', prefix: '€', min: 0, step: 1000 },
  { key: 'futureCapexYear', label: 'Capex year', min: 0, step: 1 },
  { key: 'futureCapexDurationYears', label: 'Capex duration', suffix: 'years', min: 1, step: 1 },
  { key: 'capexValueEffectRate', label: 'Capex value effect', suffix: '%', min: 0, step: 1 },
  { key: 'poolAnnualCost', label: 'Pool annual cost', prefix: '€', min: 0, step: 100 },
  { key: 'poolFutureCapex', label: 'Pool future capex', prefix: '€', min: 0, step: 1000 },
  { key: 'poolFutureCapexYear', label: 'Pool capex year', min: 0, step: 1 },
  { key: 'saunaFutureCapex', label: 'Sauna future capex', prefix: '€', min: 0, step: 1000 },
  { key: 'pvBatteryReplacementCost', label: 'Battery replacement', prefix: '€', min: 0, step: 1000 },
  { key: 'pvBatteryReplacementYear', label: 'Battery year', min: 0, step: 1 },
  { key: 'inverterReplacementCost', label: 'Inverter replacement', prefix: '€', min: 0, step: 500 },
  { key: 'inverterReplacementYear', label: 'Inverter year', min: 0, step: 1 },
  { key: 'kitchenFurnitureDepreciationMonthly', label: 'Kitchen/furniture dep.', prefix: '€', min: 0, step: 10 }
];

const housingFields: Field[] = [
  { key: 'currentRentSq', label: 'SQ rent', prefix: '€', min: 0, step: 10 },
  { key: 'targetRentZm', label: 'Object comp rent', prefix: '€', min: 0, step: 10 },
  { key: 'ownerCostsMonthly', label: 'Owner costs', prefix: '€', min: 0, step: 10 },
  { key: 'recurringCapexMonthly', label: 'Recurring capex', prefix: '€', min: 0, step: 10 },
  { key: 'savingsBeforeHousingMonthly', label: 'Savings before housing', prefix: '€', step: 50 },
  { key: 'minimumBuyPathSavingsMonthly', label: 'Minimum buy savings', prefix: '€', step: 50 }
];

const longTermPlanningFields: Field[] = [
  { key: 'retirementYear', label: 'Rentenbeginn ab Jahr', suffix: 'Jahre', min: 0, step: 1 },
  {
    key: 'postRetirementSavingsBeforeHousingMonthly',
    label: 'Sparrate ab Rente',
    prefix: '€',
    step: 50
  }
];

const rentalFields: Field[] = [
  { key: 'rentalColdRentMonthly', label: 'Cold rent', prefix: '€', min: 0, step: 10 },
  { key: 'rentalNonRecoverableCostsMonthly', label: 'Non-recoverable', prefix: '€', min: 0, step: 10 },
  { key: 'rentalMaintenanceMonthly', label: 'Rental maintenance', prefix: '€', min: 0, step: 10 },
  { key: 'rentalVacancyRate', label: 'Vacancy', suffix: '%', min: 0, step: 0.1 },
  { key: 'rentalIncomeTaxRate', label: 'Rental tax', suffix: '%', min: 0, step: 0.1 },
  { key: 'rentalBuildingValueShare', label: 'Building share', suffix: '%', min: 0, step: 1 },
  { key: 'rentalDepreciationRate', label: 'AfA', suffix: '%', min: 0, step: 0.1 },
  { key: 'rentalAreaM2', label: 'ELW area', suffix: 'm²', min: 0, step: 1 },
  { key: 'netElwCashflowMonthly', label: 'Net ELW CF', prefix: '€', min: 0, step: 10 },
  { key: 'elwColdRentMonthly', label: 'ELW cold rent', prefix: '€', min: 0, step: 10 },
  { key: 'elwNonRecoverableCostsMonthly', label: 'ELW non-recoverable', prefix: '€', min: 0, step: 10 },
  { key: 'elwMaintenanceMonthly', label: 'ELW maintenance', prefix: '€', min: 0, step: 10 },
  { key: 'elwVacancyRate', label: 'ELW vacancy', suffix: '%', min: 0, step: 0.1 },
  { key: 'elwTaxRate', label: 'ELW tax', suffix: '%', min: 0, step: 0.1 },
  { key: 'elwLegalRentabilityHaircut', label: 'ELW legal haircut', suffix: '%', min: 0, step: 1 },
  { key: 'landValueShare', label: 'Land share', suffix: '%', min: 0, step: 1 },
  { key: 'buildingValueShare', label: 'Building share ELW', suffix: '%', min: 0, step: 1 },
  { key: 'rentalBuildingShare', label: 'Rented building share', suffix: '%', min: 0, step: 1 },
  { key: 'rentalDebtInterestShare', label: 'Manual interest share', suffix: '%', min: 0, step: 1 },
  { key: 'rentalAllocatedLoanAmount', label: 'Allocated ELW loan', prefix: '€', min: 0, step: 1000 },
  { key: 'mixedUseSelfUseYears', label: 'Self-use before rent', suffix: 'years', min: 0, step: 1 }
];

const energyFields: Field[] = [
  { key: 'livingAreaM2', label: 'Living area', suffix: 'm²', min: 0, step: 1 },
  { key: 'energyCertificateAreaM2', label: 'Energy cert area', suffix: 'm²', min: 0, step: 1 },
  { key: 'currentHeatingCostMonthly', label: 'Current heating', prefix: '€', min: 0, step: 10 },
  { key: 'currentCoolingKwhPerActiveMonth', label: 'Cooling/month', suffix: 'kWh', min: 0, step: 10 },
  { key: 'currentCoolingActiveMonths', label: 'Cooling months', min: 0, step: 1 },
  { key: 'heatDemandKwhM2a', label: 'Heat demand', suffix: 'kWh/m²a', min: 0, step: 1 },
  { key: 'heatPumpJaz', label: 'Heat-pump JAZ', min: 1, step: 0.1 },
  { key: 'auxiliaryElectricityKwhYear', label: 'Heat-pump auxiliary', suffix: 'kWh/year', min: 0, step: 50 },
  { key: 'candidateEnergyDemandKwhM2a', label: 'Legacy heat demand', suffix: 'kWh/m²a', min: 0, step: 1 },
  { key: 'candidateEnergyPriceEurKwh', label: 'Energy price override', prefix: '€', min: 0, step: 0.01 },
  { key: 'electricityPriceEurKwh', label: 'Electricity price', prefix: '€', min: 0, step: 0.01 },
  { key: 'pvBenefitMonthly', label: 'PV benefit (legacy, permanent)', prefix: '€/mo', min: 0, step: 10 },
  { key: 'energyCostGrowthRate', label: 'Energy growth', suffix: '%', step: 0.1 },
  { key: 'co2PriceEurTon', label: 'CO₂ price', prefix: '€', min: 0, step: 5 },
  { key: 'co2PriceGrowthRate', label: 'CO₂ growth', suffix: '%', step: 0.1 }
];

const capitalFields: Field[] = [
  { key: 'availableEquity', label: 'Available equity', prefix: '€', min: 0, step: 1000 },
  { key: 'minimumReserve', label: 'Minimum reserve', prefix: '€', min: 0, step: 1000 },
  { key: 'cashReserveTarget', label: 'Cash reserve target', prefix: '€', min: 0, step: 1000 },
  { key: 'etfReturnRate', label: 'ETF return', suffix: '%', step: 0.1 },
  { key: 'etfTaxRate', label: 'ETF tax on gains', suffix: '%', min: 0, step: 0.1 },
  { key: 'inflationRate', label: 'Inflation', suffix: '%', step: 0.1 },
  { key: 'savingsReturnRate', label: 'TG return', suffix: '%', step: 0.1 },
  { key: 'fcfGrowthRate', label: 'FCF growth', suffix: '%', step: 0.1 },
  { key: 'rentGrowthRate', label: 'Rent growth', suffix: '%', step: 0.1 },
  { key: 'ownerCostsGrowthRate', label: 'Owner-cost growth', suffix: '%', step: 0.1 },
  { key: 'recurringCapexGrowthRate', label: 'Capex growth', suffix: '%', step: 0.1 },
  { key: 'savingsCapacityGrowthRate', label: 'Savings growth', suffix: '%', step: 0.1 },
  { key: 'propertyAppreciationRate', label: 'Property appreciation', suffix: '%', step: 0.1 }
];

const financingFields: Field[] = [
  { key: 'loanInterestRate', label: 'Loan interest', suffix: '%', min: 0, step: 0.1 },
  { key: 'initialRepaymentRate', label: 'Initial repayment', suffix: '%', min: 0, step: 0.1 },
  { key: 'comparisonInitialRepaymentRate', label: 'Compare repayment', suffix: '%', min: 0, step: 0.1 },
  { key: 'fixedRateYears', label: 'Fixed rate', suffix: 'years', min: 0, step: 1 },
  { key: 'refinanceInterestRate', label: 'Refi interest', suffix: '%', min: 0, step: 0.1 },
  { key: 'refinanceStressRate', label: 'Refi stress', suffix: '%', min: 0, step: 0.1 },
  { key: 'refinanceRepaymentRate', label: 'Refi repayment', suffix: '%', min: 0, step: 0.1 },
  { key: 'sellingCostRate', label: 'Selling costs', suffix: '%', min: 0, step: 0.1 },
  { key: 'horizonYears', label: 'Horizon', suffix: 'years', min: 1, step: 1 },
  { key: 'searchMinPrice', label: 'Search min', prefix: '€', min: 0, step: 10000 },
  { key: 'searchMaxPrice', label: 'Search max', prefix: '€', min: 0, step: 10000 }
];

const coreAssumptionFields: Field[] = [
  purchaseFields[0], purchaseFields[1], purchaseFields[2],
  energyFields[0],
  housingFields[0], housingFields[1], housingFields[2], housingFields[3], housingFields[4],
  capitalFields[0], capitalFields[1],
  financingFields[0], financingFields[1], financingFields[3], financingFields[4], financingFields[5], financingFields[6], financingFields[8]
];
const coreAssumptionKeys = new Set(coreAssumptionFields.map((field) => field.key));
const withoutCore = (fields: Field[]) => fields.filter((field) => !coreAssumptionKeys.has(field.key));

export function LegacyCalculator({ session, research }: { session?: User | null; research?: { listingId: string; runId: string; archive?: ArchivedResearchReference } } = {}) {
  const [user, setUser] = useState<User | null>(null);
  const [defaults, setDefaults] = useState<DomainInput>(fallbackDomainInput);
  const [input, setInput] = useState<DomainInput>(fallbackDomainInput);
  const [result, setResult] = useState<DomainOutput | null>(null);
  const [calculatedInput, setCalculatedInput] = useState<DomainInput | null>(null);
  const [matrixObjects, setMatrixObjects] = useState<ObjectMatrixEntry[]>([]);
  const [defaultsError, setDefaultsError] = useState<string | null>(null);
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [defaultsReady, setDefaultsReady] = useState(false);
  const [defaultsScope, setDefaultsScope] = useState<ResearchScope | null>(null);
  const edits = useRef(0), inputRef = useRef(input);
  const activeUser = session === undefined ? user : session;
  const zmEtf = result?.views.find((view) => view.key === 'ZM-ETF') ?? result?.views[0] ?? null;
  const sqEtf = result?.views.find((view) => view.key === 'SQ-ETF') ?? result?.views[0] ?? null;

  useEffect(() => {
    // Old drafts have no actor/property provenance and must never be read/applied.
    try { localStorage.removeItem('calculator-research-draft'); } catch { /* storage may be unavailable */ }
    if (session !== undefined) return;
    let live = true;
    currentUser().then(value => { if (live) setUser(value); }).catch(error => { if (live) setDefaultsError(error instanceof Error ? error.message : 'Login failed'); });
    return () => { live = false; };
  }, [session]);

  useEffect(() => {
    const controller = new AbortController();
    const version = edits.current;
    (activeUser ? readCalculatorDefaults(activeUser, controller.signal) : getDomainDefaults(controller.signal).then(value => ({ input: value, scope: null })))
      .then(({ input: loadedDefaults, scope }) => {
        if (controller.signal.aborted) return;
        setDefaults(loadedDefaults); setDefaultsScope(scope); setDefaultsReady(true);
        if (version === edits.current) { inputRef.current = loadedDefaults; setInput(loadedDefaults); }
        setDefaultsError(null);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        setDefaultsError(error instanceof Error ? error.message : 'Loading defaults failed');
      });

    return () => controller.abort();
  }, [activeUser]);

  useEffect(() => {
    const controller = new AbortController();
    calculateDomain(input, controller.signal)
      .then((output) => {
        if (controller.signal.aborted) return;
        setResult(output);
        setCalculatedInput(input);
        setCalculationError(null);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
          return;
        }
        setCalculationError(error instanceof Error ? error.message : 'Calculation failed');
      });

    return () => controller.abort();
  }, [input]);

  function editInput(next: DomainInput | ((current: DomainInput) => DomainInput)) {
    const value = typeof next === 'function' ? next(inputRef.current) : next;
    edits.current++; inputRef.current = value; setInput(value);
  }
  function applyResearch(values: ResearchValues, baseline: DomainInput) {
    if (!defaultsReady || inputRef.current !== baseline) return false;
    editInput({ ...baseline, ...values }); return true;
  }
  function updateField(key: NumericDomainInputKey, value: string) {
    const numericValue = Number(value);
    editInput((current) => ({
      ...current,
      [key]: Number.isFinite(numericValue) ? numericValue : 0
    }));
  }

  function updateFinancingRatio(value: string) {
    const numericValue = Number(value);
    editInput((current) => ({
      ...current,
      financingRatio: value.trim() === '' || !Number.isFinite(numericValue) ? null : numericValue
    }));
  }

  return (
    <main className="app-shell">
      {research && activeUser && <ResearchDraftReview user={activeUser} listingId={research.listingId} runId={research.runId} archive={research.archive} input={input} scope={defaultsScope} ready={defaultsReady} getInput={() => inputRef.current} onApply={applyResearch} />}
      <section className="summary-band">
        <div>
          <p className="eyebrow">Haushalt · Objekt · Entscheidung</p>
          <h1>Immobilien-Rechner</h1>
          <p>Erst Kernannahmen prüfen, dann Preis, monatliche Tragfähigkeit und Refinanzierung bewerten.</p>
        </div>
        <div className="summary-grid" aria-label="Entscheidungsübersicht">
          <Metric label="Zielpreis" value={result ? formatOptionalCurrency(result.priceGuidance.targetPurchasePrice) : '…'} tone={result?.priceGuidance.requiredDiscountRate ? 'negative' : 'neutral'} />
          <Metric label="Harte Obergrenze" value={result ? formatOptionalCurrency(result.priceGuidance.hardMaxPrice) : '…'} />
          <Metric label="ETF-neutraler Preis" value={result ? formatOptionalCurrency(result.priceGuidance.etfNeutralPrice) : '…'} />
          <Metric label="Vermögensdelta ggü. Miete + ETF" value={result ? formatSignedCurrency(result.wealthDeltaVsCurrentRentEtfContext.nominalWealthDeltaAtHorizon) : '…'} suffix={result ? formatSignedPercent(result.wealthDeltaVsCurrentRentEtfContext.percentOfBenchmarkTotal) : undefined} />
          <Metric label="Monatliche Kreditrate" value={zmEtf ? formatCurrency(zmEtf.monthlyPayment) : '…'} suffix="/Monat" />
          <Metric label="Restschuld Jahr 10 / Refi-Stress" value={zmEtf ? formatCurrency(zmEtf.remainingDebtYear10) : '…'} suffix={`${input.refinanceStressRate.toFixed(1)} % nach ${input.fixedRateYears} J.`} tone={zmEtf && zmEtf.remainingDebtYear10 > 0 ? 'negative' : 'neutral'} />
        </div>
      </section>


      <section className="workspace" aria-label="Calculator workspace">
        <div id="calculator" className="inputs-column">
          <div className="section-heading">
            <p className="eyebrow">Property model</p>
            <h2>Calculator assumptions</h2>
            <p>Open only the groups you want to review. Results update automatically.</p>
          </div>
          <Fieldset title="Kernannahmen" fields={coreAssumptionFields} input={input} onChange={updateField} />
          <RenovationPhaseEditor
            phases={input.renovationPhases}
            onChange={(renovationPhases) => editInput((current) => ({ ...current, renovationPhases }))}
          />
          <PvPhaseEditor
            phases={input.pvPhases}
            onChange={(pvPhases) => editInput((current) => ({ ...current, pvPhases }))}
          />
          {result?.warnings.length ? <ul className="prompt-warnings">{result.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul> : null}
          <Fieldset title="Optionale Objekt- und Capexwerte" fields={withoutCore(purchaseFields)} input={input} onChange={updateField} collapsed />
          <Fieldset title="Energie, Wärmepumpe und Kühlung" fields={withoutCore(energyFields)} input={input} onChange={updateField} collapsed />
          <Fieldset title="Vermietung / ELW" fields={rentalFields} input={input} onChange={updateField} collapsed />
          <Fieldset title="Weitere Wohn-, Kapital- und Finanzierungsannahmen" fields={[...withoutCore(housingFields), ...withoutCore(capitalFields), ...withoutCore(financingFields)]} input={input} onChange={updateField} collapsed />
          <ObjectComparisonMatrix
            householdInput={input}
            objects={matrixObjects}
            onChange={setMatrixObjects}
          />
          <CalculatorPromptPreview user={user} input={input} />
          <Fieldset
            title="Langfristige Planung"
            fields={longTermPlanningFields}
            input={input}
            onChange={updateField}
            collapsed
          />
          <AdvancedFinancing
            input={input}
            onChange={updateField}
            onFinancingRatioChange={updateFinancingRatio}
            onFinancingModeChange={(useRatio) => editInput((current) => ({
              ...current,
              financingRatio: useRatio ? current.financingRatio ?? 80 : null
            }))}
            onFinancingDefinitionChange={(financingRatioDefinition) => editInput((current) => ({
              ...current,
              financingRatioDefinition
            }))}
          />
        </div>

        <aside id="results" className="analysis-panel" aria-label="Analysis">
          <div className="panel-header">
            <div>
              <p className="eyebrow panel-eyebrow">Offer {formatCurrency(input.purchasePrice)}</p>
              <h2>Scenario output</h2>
            </div>
            <button type="button" onClick={() => editInput(defaults)}>
              Reset
            </button>
          </div>

          {defaultsError ? <p className="error-text">{defaultsError}</p> : null}
          {calculationError ? <p className="error-text">{calculationError}</p> : null}
          <FutureOptionalityPanel views={result?.views ?? []} current={calculatedInput === input && !calculationError} />
          {result ? (
            <>
              <ScenarioTable result={result} />
              <details className="result-details"><summary>Vermögensvergleich, Tilgung und weitere Szenarien</summary>
                <WealthDeltaTable result={result} />
                <AsymptoticFamilyWealthTable result={result} />
                <RepaymentComparisonPanel comparison={result.repaymentComparison} />
                <RentalTable result={result} />
                <MixedUseTable result={result} />
              </details>
            </>
          ) : (
            <p className="loading-text">Calculating...</p>
          )}


          {result && zmEtf && sqEtf ? (
            <details className="result-details"><summary>Alle Detailkennzahlen</summary><div className="detail-list">
              <Detail label="Target purchase price" value={formatOptionalCurrency(result.priceGuidance.targetPurchasePrice)} strong />
              <Detail label="Hard maximum price" value={formatOptionalCurrency(result.priceGuidance.hardMaxPrice)} strong />
              <Detail label="ETF-neutral price" value={formatOptionalCurrency(result.priceGuidance.etfNeutralPrice)} />
              <Detail label="Required discount" value={formatNullablePercent(result.priceGuidance.requiredDiscountRate)} />
              <Detail label="Rent equivalent 30y" value={formatOptionalCurrency(result.rentEquivalent30Monthly)} strong />
              <Detail label="Rent equivalent 50y" value={formatOptionalCurrency(result.rentEquivalent50Monthly)} />
              <Detail label="Comparison-rent gross yield" value={formatNullablePercent(result.comparisonRentYield.grossYieldOnTotalCost)} strong />
              <Detail label="Comparison-rent net yield" value={formatNullablePercent(result.comparisonRentYield.netYieldOnTotalCost)} strong />
              <Detail label="Comparison-rent NOI" value={formatCurrency(result.comparisonRentYield.netOperatingIncomeMonthly)} />
              <Detail label="SQ monthly FCF" value={formatCurrency(sqEtf.freeCashFlowMonthly)} />
              <Detail label="ZM monthly FCF" value={formatCurrency(zmEtf.freeCashFlowMonthly)} />
              <Detail label="ETF housing premium" value={formatCurrency(result.housingChangePremiumIntrinsicEtf)} strong />
              <Detail label="TG housing premium" value={formatCurrency(result.housingChangePremiumIntrinsicTg)} />
              <Detail label="SQ benchmark premium" value={formatCurrency(result.benchmarkPremiumSq)} />
              <Detail label="ZM benchmark premium" value={formatCurrency(result.benchmarkPremiumZm)} />
              <Detail label="ETF capex haircut" value={formatCurrency(result.capexHaircutEtf)} />
              <Detail label="TG capex haircut" value={formatCurrency(result.capexHaircutTg)} />
              <Detail label="ELW after-tax CF" value={formatCurrency(result.partialRental.rentalCashflowAfterTax)} strong />
              <Detail label="ELW taxable income" value={formatCurrency(result.partialRental.rentalTaxableIncome)} />
              <Detail label="ELW tax effect" value={formatCurrency(result.partialRental.rentalTaxEffect)} />
              <Detail label="ELW required net CF" value={formatOptionalCurrency(result.requiredNetElwCashflowForSqEtfNeutrality)} strong />
              <Detail label="SQ vs rent+ETF" value={formatWealthDeltaCompact(result.wealthDeltaVsCurrentRentEtfContext)} strong />
              <Detail label="Current energy" value={formatCurrency(zmEtf.currentEnergyCostMonthly)} />
              <Detail label="Candidate energy" value={formatCurrency(zmEtf.candidateEnergyCostMonthly)} />
              <Detail label="Energy savings" value={formatCurrency(zmEtf.energySavingsMonthly)} strong />
              <Detail label="ZM strategy value" value={formatCurrency(zmEtf.totalStrategyValue)} strong />
              <Detail label="ZM savings cap" value={formatOptionalCurrency(zmEtf.savingsRateCap)} />
              <Detail label="ZM rest-debt cap" value={formatOptionalCurrency(zmEtf.debtResidualCap)} />
              <Detail label="Min buy savings" value={formatCurrency(zmEtf.minimumBuyPathSavingsMonthly)} />
              <Detail label="Terminal property equity" value={formatCurrency(zmEtf.terminalPropertyEquity)} />
              <Detail label="Loan at offer" value={formatCurrency(zmEtf.initialLoan)} />
              <Detail label="Financing basis" value={formatFinancingBasis(zmEtf)} />
              <Detail label="All-in initial cost" value={formatCurrency(zmEtf.allInInitialCost)} />
              <Detail label="Loan / purchase price" value={formatNullablePercent(zmEtf.loanToPurchasePriceRatio)} />
              <Detail label="Loan / all-in cost" value={formatNullablePercent(zmEtf.loanToAllInCostRatio)} />
              <Detail label="Equity into purchase" value={formatCurrency(zmEtf.equityInvested)} />
              <Detail label="Initial cash reserve" value={formatCurrency(zmEtf.cashReserveInitial)} />
              <Detail label="Cash reserve return" value={formatNullablePercent(zmEtf.cashReserveReturnRate)} />
              <Detail label="Initial ETF sidecar" value={formatCurrency(zmEtf.etfSidecarInitial)} />
              <Detail label="ETF sidecar at horizon" value={formatCurrency(zmEtf.etfSidecarTerminalValue)} />
              <Detail label="ETF depot sale (gross)" value={formatCurrency(zmEtf.existingDepotWithdrawalGross)} />
              <Detail label="ETF depot proceeds (net)" value={formatCurrency(zmEtf.existingDepotWithdrawalNet)} />
              <Detail label="Funding gap" value={formatSignedCurrency(zmEtf.fundingGap)} strong={!zmEtf.financingFeasible} />
              <Detail label="Liquidity shortfall" value={formatSignedCurrency(zmEtf.liquidityShortfall)} strong={!zmEtf.financingFeasible} />
              <Detail label="Monthly payment" value={formatCurrency(zmEtf.monthlyPayment)} />
              <Detail label="Loan payoff" value={formatPayoffMonth(zmEtf.loanPayoffMonth)} />
              <Detail label="Loan interest to horizon" value={formatCurrency(zmEtf.loanTotalInterest)} />
              <Detail label="Cash reserve at horizon" value={formatCurrency(zmEtf.cashReserveAtHorizon)} />
              <Detail label="Investment account at horizon" value={formatCurrency(zmEtf.investmentAccountAtHorizon)} />
              <Detail label="Rest debt 10y" value={formatCurrency(zmEtf.remainingDebtYear10)} />
              <Detail label="Rest debt 30y" value={formatCurrency(zmEtf.remainingDebtYear30)} />
              <Detail label="Rest debt 50y" value={formatCurrency(zmEtf.remainingDebtYear50)} />
            </div></details>
          ) : null}
        </aside>
      </section>
    </main>
  );
}

type PropertyResearchQueueItem = {
  id: string;
  listingId: string;
  priority: number;
  status: 'pending' | 'leased' | 'completed' | 'skipped' | 'cancelled';
  attempts: number;
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  supersedesId?: string | null;
  delegatedDeadline?: number | null;
  leaseExpiresAt?: string | null;
};

type PropertyResearchRun = {
  id: string;
  listingId: string;
  queueItemId?: string | null;
  modelIdentifier: string;
  status: 'draft' | 'approved' | 'superseded';
  structuredData: unknown;
  provenance: unknown;
  evidence: unknown;
  createdAt: string;
  approvedAt?: string | null;
};

type PropertyResearchCohortProgress = {
  modelIdentifier: string;
  pending: number;
  leased: number;
  completed: number;
  skipped: number;
  cancelled: number;
};

type PropertyResearchCohort = {
  id: string;
  label?: string | null;
  selectionKind: string;
  requestedCount: number;
  selectedCount: number;
  modelIdentifiers: string[];
  frozenAt: string;
  createdAt: string;
  progress: PropertyResearchCohortProgress[];
};

function compactId(value: string) {
  return value.length > 14 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
}

export function PropertyResearchDashboard({ user }: { user: User | null }) {
  const [queue, setQueue] = useState<PropertyResearchQueueItem[]>([]);
  const [runs, setRuns] = useState<PropertyResearchRun[]>([]);
  const [cohorts, setCohorts] = useState<PropertyResearchCohort[]>([]);
  const [listingIds, setListingIds] = useState('');
  const [priority, setPriority] = useState(100);
  const [cohortModels, setCohortModels] = useState('openai/gpt-5.6-terra, openai/gpt-5.6-sol');
  const [cohortSize, setCohortSize] = useState(10);
  const [cohortLabel, setCohortLabel] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [queueObservedAt, setQueueObservedAt] = useState(0);

  const load = useCallback(async () => {
    if (!user) return;
    const [nextQueue, nextRuns, nextCohorts] = await Promise.all([
      authFetch(user, '/api/research/property-research/queue?limit=100'),
      authFetch(user, '/api/research/property-research/runs?limit=100'),
      authFetch(user, '/api/research/property-research/cohorts?limit=20')
    ]);
    const observedAt = Date.now();
    setQueue(nextQueue as PropertyResearchQueueItem[]);
    setRuns(nextRuns as PropertyResearchRun[]);
    setCohorts(nextCohorts as PropertyResearchCohort[]);
    setQueueObservedAt(observedAt);
    setUpdatedAt(new Date(observedAt).toLocaleTimeString());
    setError(null);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const poll = async () => {
      try {
        const [nextQueue, nextRuns, nextCohorts] = await Promise.all([
          authFetch(user, '/api/research/property-research/queue?limit=100'),
          authFetch(user, '/api/research/property-research/runs?limit=100'),
          authFetch(user, '/api/research/property-research/cohorts?limit=20')
        ]);
        if (!active) return;
        const observedAt = Date.now();
        setQueue(nextQueue as PropertyResearchQueueItem[]);
        setRuns(nextRuns as PropertyResearchRun[]);
        setCohorts(nextCohorts as PropertyResearchCohort[]);
        setQueueObservedAt(observedAt);
        setUpdatedAt(new Date(observedAt).toLocaleTimeString());
        setError(null);
      } catch (reason) {
        if (active) setError(reason instanceof Error ? reason.message : 'Research data could not be loaded');
      }
    };
    const initial = window.setTimeout(() => void poll(), 0);
    const interval = window.setInterval(() => void poll(), 15_000);
    return () => {
      active = false;
      window.clearTimeout(initial);
      window.clearInterval(interval);
    };
  }, [user]);

  const enqueue = async () => {
    if (!user) return;
    const ids = listingIds.split(/[\s,;]+/).map((value) => value.trim()).filter(Boolean);
    if (!ids.length) {
      setError('Enter at least one listing ID.');
      return;
    }
    setPending('enqueue');
    try {
      await authFetch(user, '/api/research/property-research/queue', {
        method: 'POST',
        body: JSON.stringify({ listingIds: ids, priority })
      });
      setListingIds('');
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Queue update failed');
    } finally {
      setPending(null);
    }
  };

  const freshQueueWork = async (item: PropertyResearchQueueItem) => {
    if (!user || !isDelegated(user)) return;
    setPending(`fresh:${item.id}`);
    try {
      await authFetch(user, `/api/research/property-research/queue/${encodeURIComponent(item.id)}/fresh`, {
        method: 'POST', body: JSON.stringify({ priority })
      });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Fresh work could not be queued; refresh the queue and obtain a new handoff.');
    } finally {
      setPending(null);
    }
  };

  const approve = async (runId: string) => {
    if (!user) return;
    setPending(`approve:${runId}`);
    try {
      await authFetch(user, `/api/research/property-research/runs/${runId}/approve`, { method: 'POST' });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Approval failed');
    } finally {
      setPending(null);
    }
  };

  const createCohort = async () => {
    if (!user) return;
    const modelIdentifiers = cohortModels.split(/[\n,;]+/).map((value) => value.trim()).filter(Boolean);
    if (!modelIdentifiers.length) {
      setError('Enter at least one model identifier.');
      return;
    }
    setPending('cohort');
    try {
      await authFetch(user, '/api/research/property-research/cohorts', {
        method: 'POST',
        body: JSON.stringify({ maxItems: cohortSize, modelIdentifiers, label: cohortLabel || undefined })
      });
      setCohortLabel('');
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Comparison cohort creation failed');
    } finally {
      setPending(null);
    }
  };
  const cancelCohort = async (cohortId: string) => {
    if (!user) return;
    setPending(`cancel:${cohortId}`);
    try {
      await authFetch(user, `/api/research/property-research/cohorts/${cohortId}/cancel`, { method: 'POST' });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Comparison cohort cancellation failed');
    } finally {
      setPending(null);
    }
  };

  const counts = queue.reduce<Record<string, number>>((result, item) => {
    result[item.status] = (result[item.status] ?? 0) + 1;
    return result;
  }, {});

  return <section id="research-operations" className="research-operations" aria-label="Property research operations">
    <div className="panel-header">
      <div><p className="eyebrow">Structured object research</p><h2>Research queue and model runs</h2></div>
      <button type="button" className="secondary-action" disabled={!user || pending !== null} onClick={() => void load()}>Refresh</button>
    </div>
    {!user ? <p>Sign in above to review private research runs.</p> : <>
      <div className="research-summary" aria-label="Research queue status">
        <span><strong>{counts.pending ?? 0}</strong> queued</span>
        <span><strong>{counts.leased ?? 0}</strong> in review</span>
        <span><strong>{counts.completed ?? 0}</strong> completed</span>
        <span><strong>{runs.filter((run) => run.status === 'draft').length}</strong> drafts</span>
        <span><strong>{runs.filter((run) => run.status === 'approved').length}</strong> approved</span>
      </div>
      <div className="research-enqueue">
        <label>Listing IDs<textarea value={listingIds} onChange={(event) => setListingIds(event.target.value)} placeholder="One or more listing IDs" rows={2} /></label>
        <label>Priority<input type="number" min="-10000" max="10000" value={priority} onChange={(event) => setPriority(Number(event.target.value))} /></label>
        <button type="button" disabled={pending !== null} onClick={() => void enqueue()}>{pending === 'enqueue' ? 'Queueing...' : 'Add to queue'}</button>
      </div>
      <div className="research-enqueue" aria-label="Frozen model comparison cohort">
        <label>Comparison models<textarea value={cohortModels} onChange={(event) => setCohortModels(event.target.value)} placeholder="one exact model identifier per comma" rows={2} /></label>
        <label>Frozen objects<input type="number" min="1" max="100" value={cohortSize} onChange={(event) => setCohortSize(Number(event.target.value))} /></label>
        <label>Label <small>(optional)</small><input value={cohortLabel} onChange={(event) => setCohortLabel(event.target.value)} placeholder="e.g. July quality comparison" /></label>
        <button type="button" disabled={pending !== null} onClick={() => void createCohort()}>{pending === 'cohort' ? 'Freezing...' : 'Freeze model comparison set'}</button>
      </div>
      <div className="research-list" aria-label="Frozen model comparison cohorts">
        <h3>Frozen model comparison cohorts</h3>
        {cohorts.length ? cohorts.map((cohort) => <article key={cohort.id} className="research-row">
          <div><strong>{cohort.label || `Cohort ${compactId(cohort.id)}`}</strong><small>{cohort.selectedCount}/{cohort.requestedCount} newest active discoveries · frozen {new Date(cohort.frozenAt).toLocaleString()}</small><small title={cohort.id}>{compactId(cohort.id)} · versions are fixed; later listings do not enter</small></div>
          <div className="research-run-actions">{cohort.progress.map((progress) => <small key={progress.modelIdentifier}><strong>{progress.modelIdentifier}</strong> {progress.completed + progress.skipped}/{cohort.selectedCount} done · {progress.leased} leased</small>)}{cohort.progress.some((progress) => progress.pending > 0 || progress.leased > 0) ? <button type="button" disabled={pending !== null} onClick={() => void cancelCohort(cohort.id)}>{pending === `cancel:${cohort.id}` ? 'Closing...' : 'Close'}</button> : null}</div>
        </article>) : <p className="loading-text">No frozen comparison cohort yet.</p>}
      </div>
      {error ? <p className="error-text">{error}</p> : null}
      <div className="research-grid">
        <div>
          <h3>Queue</h3>
          <small>Expired delegated work needs a fresh handoff and the explicit fresh-work action. Existing history and live leases are never replaced.</small>
          <div className="research-list">{queue.length ? queue.map((item) => <article key={item.id} className="research-row">
            <div><strong title={item.listingId}>{compactId(item.listingId)}</strong><small>Priority {item.priority} · {item.attempts} attempts</small></div>
            <div className="research-run-actions"><span className={`decision ${item.status === 'leased' ? 'inspect' : item.status === 'completed' ? 'inspect' : item.status === 'skipped' || item.status === 'cancelled' ? 'reject' : 'negotiate'}`}>{item.status}</span>{isDelegated(user) && (['completed', 'skipped', 'cancelled'].includes(item.status) || (item.delegatedDeadline != null && item.delegatedDeadline * 1000 <= queueObservedAt)) && (!item.leaseExpiresAt || Date.parse(item.leaseExpiresAt) <= queueObservedAt) ? <button type="button" disabled={pending !== null} onClick={() => void freshQueueWork(item)}>{pending === `fresh:${item.id}` ? 'Queueing...' : 'Queue fresh work'}</button> : null}</div>
          </article>) : <p className="loading-text">No queued objects.</p>}</div>
        </div>
        <div>
          <h3>Model runs</h3>
          <div className="research-list">{runs.length ? runs.map((run) => <article key={run.id} className="research-row research-run">
            <div><strong>{run.modelIdentifier}</strong><small title={run.listingId}>{compactId(run.listingId)} · {new Date(run.createdAt).toLocaleString()}</small></div>
            <div className="research-run-actions"><span className={`decision ${run.status === 'approved' ? 'inspect' : run.status === 'draft' ? 'negotiate' : 'reject'}`}>{run.status}</span>{run.status === 'draft' ? <button type="button" disabled={pending !== null} onClick={() => void approve(run.id)}>{pending === `approve:${run.id}` ? 'Approving...' : 'Approve'}</button> : null}</div>
          </article>) : <p className="loading-text">No model runs yet.</p>}</div>
        </div>
      </div>
      {updatedAt ? <small className="research-updated">Updated {updatedAt}</small> : null}
    </>}
  </section>;
}

export { CrawlDashboard } from './CrawlDashboard';

function RentalTable({ result }: { result: DomainOutput }) {
  return (
    <div className="scenario-table" role="table" aria-label="Rental scenario results">
      <div className="scenario-row header" role="row">
        <span>Rental</span>
        <span>NOI</span>
        <span>AT CF</span>
        <span>Max</span>
        <span>Delta context</span>
        <span>SI</span>
        <span>Decision</span>
      </div>
      {result.rentalViews.map((view) => (
        <RentalRow key={view.key} view={view} />
      ))}
    </div>
  );
}

function RentalRow({ view }: { view: RentalViewResult }) {
  return (
    <div className="scenario-row" role="row">
      <strong>{view.key}</strong>
      <span>{formatCurrency(view.netOperatingIncomeMonthly)}</span>
      <span>{formatCurrency(view.afterTaxCashFlowAfterFinancingMonthly)}</span>
      <span>{formatOptionalCurrency(view.maximumLeveragedPrice)}</span>
      <span>{formatWealthDeltaCompact(view.wealthDeltaContext)}</span>
      <span>{formatOptionalNumber(view.strategyIndex)}</span>
      <span className={`decision ${view.decision}`}>{view.decision}</span>
    </div>
  );
}

function MixedUseTable({ result }: { result: DomainOutput }) {
  if (result.mixedUseViews.length === 0) {
    return null;
  }

  return (
    <div className="scenario-table" role="table" aria-label="Mixed-use scenario results">
      <div className="scenario-row header" role="row">
        <span>Mixed</span>
        <span>Self-use</span>
        <span>Rent CF</span>
        <span>Max</span>
        <span>Delta context</span>
        <span>SI</span>
        <span>Decision</span>
      </div>
      {result.mixedUseViews.map((view) => (
        <MixedUseRow key={view.key} view={view} />
      ))}
    </div>
  );
}

function MixedUseRow({ view }: { view: MixedUseViewResult }) {
  return (
    <div className="scenario-row" role="row">
      <strong>{view.key}</strong>
      <span>{view.selfUseYears}y</span>
      <span>{formatCurrency(view.rentalPhaseSavingsMonthly)}</span>
      <span>{formatOptionalCurrency(view.maximumLeveragedPrice)}</span>
      <span>{formatWealthDeltaCompact(view.wealthDeltaContext)}</span>
      <span>{formatOptionalNumber(view.strategyIndex)}</span>
      <span className={`decision ${view.decision}`}>{view.decision}</span>
    </div>
  );
}

const newPvPhase = (startMonth: number): PvPhase => ({
  startMonth,
  endMonth: null,
  monthlyBenefit: 0,
  growthRate: 0
});

function PvPhaseEditor({ phases, onChange }: { phases: PvPhase[]; onChange: (phases: PvPhase[]) => void }) {
  const update = <Key extends keyof PvPhase>(index: number, key: Key, value: PvPhase[Key]) => {
    onChange(phases.map((phase, currentIndex) => currentIndex === index ? { ...phase, [key]: value } : phase));
  };
  const updateNumber = (index: number, key: 'startMonth' | 'monthlyBenefit' | 'growthRate', value: string) => {
    const parsed = Number(value);
    update(index, key, Number.isFinite(parsed) ? (key === 'startMonth' ? Math.max(0, Math.trunc(parsed)) : parsed) : 0);
  };
  return (
    <details className="field-group renovation-phases" open={phases.length > 0}>
      <summary>PV-Nutzenphasen<span>{phases.length === 1 ? '1 Phase' : phases.length ? `${phases.length} Phasen` : 'optional'}</span></summary>
      <p className="phase-intro">Monat 0 ist der erste Monat. Start inklusive, Ende exklusiv: 488 €/Monat bis Monat 39, danach 100 €/Monat ab Monat 39. Jede Phase hat eine eigene jährliche Steigerung; 0 % hält den Betrag konstant. PV-Phasen ersetzen den alten pauschalen PV-Nutzen.</p>
      <div className="phase-list">
        {phases.map((phase, index) => (
          <article className="phase-card" key={index}>
            <div className="phase-card-header">
              <strong>PV-Phase {index + 1}</strong>
              <button type="button" className="secondary-action" onClick={() => onChange(phases.filter((_, currentIndex) => currentIndex !== index))}>Remove</button>
            </div>
            <div className="field-grid">
              <PhaseNumber label="Start month" value={phase.startMonth} min={0} step={1} onChange={(value) => updateNumber(index, 'startMonth', value)} />
              <PhaseNumber label="End month (exclusive)" value={phase.endMonth ?? ''} min={0} step={1} onChange={(value) => update(index, 'endMonth', value.trim() === '' ? null : Math.max(0, Math.trunc(Number(value) || 0)))} />
              <PhaseNumber label="Monthly benefit" value={phase.monthlyBenefit} min={0} step={1} prefix="€" onChange={(value) => updateNumber(index, 'monthlyBenefit', value)} />
              <PhaseNumber label="Annual growth" value={phase.growthRate} min={-100} step={0.1} suffix="%" onChange={(value) => updateNumber(index, 'growthRate', value)} />
            </div>
          </article>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...phases, newPvPhase(phases.at(-1)?.endMonth ?? 0)])}>Add PV phase</button>
    </details>
  );
}

const newRenovationPhase = (): RenovationPhase => ({
  name: 'New phase',
  startYear: 0,
  durationYears: 1,
  capex: 0,
  capexValueEffectRate: 0,
  energyDemandAfter: null,
  energySourceAfter: null,
  ownerCostsDeltaMonthly: 0,
  recurringCapexDeltaMonthly: 0,
  fundingSource: 'income',
  confirmedGrantAmount: 0
});

function RenovationPhaseEditor({
  phases,
  onChange
}: {
  phases: RenovationPhase[];
  onChange: (phases: RenovationPhase[]) => void;
}) {
  const update = <Key extends keyof RenovationPhase>(index: number, key: Key, value: RenovationPhase[Key]) => {
    onChange(phases.map((phase, currentIndex) => currentIndex === index ? { ...phase, [key]: value } : phase));
  };
  const updateNumber = (index: number, key: keyof Pick<RenovationPhase,
    'startYear' | 'durationYears' | 'capex' | 'capexValueEffectRate' | 'ownerCostsDeltaMonthly' | 'recurringCapexDeltaMonthly' | 'confirmedGrantAmount'
  > | 'energyDemandAfter', value: string) => {
    const parsed = Number(value);
    update(index, key, value.trim() === '' && key === 'energyDemandAfter' ? null : (Number.isFinite(parsed) ? parsed : 0));
  };

  return (
    <details className="field-group renovation-phases" open={phases.length > 0}>
      <summary>Sanierungsphasen<span>{phases.length === 1 ? '1 Phase' : phases.length ? `${phases.length} Phasen` : 'optional'}</span></summary>
      <p className="phase-intro">Eine Phase wirkt nach ihrer Dauer. Capex wird gleichmäßig über die Phasenjahre verteilt; die gewählte Quelle finanziert jede Tranche.</p>
      <div className="phase-list">
        {phases.map((phase, index) => (
          <article className="phase-card" key={`${index}-${phase.name}`}>
            <div className="phase-card-header">
              <strong>Phase {index + 1}</strong>
              <button type="button" className="secondary-action" onClick={() => onChange(phases.filter((_, currentIndex) => currentIndex !== index))}>Remove</button>
            </div>
            <div className="field-grid">
              <label className="field"><span>Name</span><div className="input-wrap"><input value={phase.name} onChange={(event) => update(index, 'name', event.target.value)} /></div></label>
              <PhaseNumber label="Start year" value={phase.startYear} min={0} step={1} onChange={(value) => updateNumber(index, 'startYear', value)} />
              <PhaseNumber label="Duration" value={phase.durationYears} min={1} step={1} suffix="years" onChange={(value) => updateNumber(index, 'durationYears', value)} />
              <PhaseNumber label="Capex" value={phase.capex} min={0} step={1000} prefix="€" onChange={(value) => updateNumber(index, 'capex', value)} />
              <PhaseNumber label="Value effect" value={phase.capexValueEffectRate} min={0} step={1} suffix="%" onChange={(value) => updateNumber(index, 'capexValueEffectRate', value)} />
              <PhaseNumber label="Heat demand after" value={phase.energyDemandAfter ?? ''} min={0} step={1} suffix="kWh/m²a" onChange={(value) => updateNumber(index, 'energyDemandAfter', value)} />
              <label className="field"><span>Energy after</span><select value={phase.energySourceAfter ?? ''} onChange={(event) => update(index, 'energySourceAfter', event.target.value === '' ? null : event.target.value as RenovationPhase['energySourceAfter'])}><option value="">No change</option><option value="oil">Oil</option><option value="gas">Gas</option><option value="district_heat">District heat</option><option value="heat_pump">Heat pump</option><option value="electricity">Electricity</option><option value="pellets">Pellets</option></select></label>
              <PhaseNumber label="Owner costs delta" value={phase.ownerCostsDeltaMonthly} step={10} prefix="€/mo" onChange={(value) => updateNumber(index, 'ownerCostsDeltaMonthly', value)} />
              <PhaseNumber label="Recurring capex delta" value={phase.recurringCapexDeltaMonthly} step={10} prefix="€/mo" onChange={(value) => updateNumber(index, 'recurringCapexDeltaMonthly', value)} />
              <label className="field"><span>Funding source</span><select value={phase.fundingSource} onChange={(event) => update(index, 'fundingSource', event.target.value as RenovationPhase['fundingSource'])}><option value="income">Current income</option><option value="cash_reserve">Cash reserve</option><option value="etf_sidecar">ETF sidecar</option><option value="renovation_loan">Renovation loan</option><option value="confirmed_grant">Confirmed grant</option></select></label>
              <PhaseNumber label="Confirmed grant" value={phase.confirmedGrantAmount} min={0} step={500} prefix="€" onChange={(value) => updateNumber(index, 'confirmedGrantAmount', value)} />
            </div>
          </article>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...phases, newRenovationPhase()])}>Add renovation phase</button>
    </details>
  );
}

function PhaseNumber({ label, value, onChange, min, step, prefix, suffix }: { label: string; value: number | ''; onChange: (value: string) => void; min?: number; step?: number; prefix?: string; suffix?: string }) {
  return <label className="field"><span>{label}</span><div className="input-wrap">{prefix ? <span className="affix">{prefix}</span> : null}<input type="number" value={value} min={min} step={step} onChange={(event) => onChange(event.target.value)} />{suffix ? <span className="affix suffix">{suffix}</span> : null}</div></label>;
}

function ObjectComparisonMatrix({
  householdInput,
  objects,
  onChange
}: {
  householdInput: DomainInput;
  objects: ObjectMatrixEntry[];
  onChange: (objects: ObjectMatrixEntry[]) => void;
}) {
  const [results, setResults] = useState<Array<DomainOutput | null>>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    if (objects.length === 0) {
      return () => controller.abort();
    }
    Promise.all(objects.map((object) => calculateDomain(matrixInput(householdInput, object.input), controller.signal)))
      .then((nextResults) => {
        setResults(nextResults);
        setError(null);
      })
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        setError(reason instanceof Error ? reason.message : 'Object matrix calculation failed');
      });
    return () => controller.abort();
  }, [householdInput, objects]);

  const addCurrentObject = () => {
    const snapshot = { ...householdInput, renovationPhases: householdInput.renovationPhases.map((phase) => ({ ...phase })), pvPhases: householdInput.pvPhases.map((phase) => ({ ...phase })) };
    onChange([...objects, {
      id: crypto.randomUUID(),
      name: `Object ${objects.length + 1}`,
      input: snapshot
    }]);
  };
  const update = (id: string, changes: Partial<ObjectMatrixEntry>) => onChange(objects.map((object) => object.id === id ? { ...object, ...changes } : object));

  return <details className="field-group object-matrix" open={objects.length > 0}>
    <summary>Mehr-Objekt-Matrix<span>{objects.length ? `${objects.length} Objekte` : 'optional'}</span></summary>
    <p className="phase-intro">Jedes gespeicherte Objekt behält seine Immobilienannahmen. Haushaltsvermögen, ETF-, Steuer-, Zins- und Zeithorizontannahmen werden immer aus dem aktuellen Rechner übernommen.</p>
    <div className="matrix-actions"><button type="button" onClick={addCurrentObject}>Current object to matrix</button></div>
    {error ? <p className="error-text">{error}</p> : null}
    {objects.length ? <div className="object-matrix-table" role="table" aria-label="Multi-property comparison matrix">
      <div className="object-matrix-row header" role="row"><span>Object</span><span>Offer</span><span>ZM-ETF delta</span><span>Max price</span><span>Debt year 10</span><span>Decision</span><span /></div>
      {objects.map((object, index) => {
        const result = objects.length === 0 ? null : results[index];
        const view = result?.views.find((candidate) => candidate.key === 'ZM-ETF') ?? null;
        return <div className="object-matrix-row" role="row" key={object.id}>
          <span><input aria-label={`Object ${index + 1} name`} value={object.name} onChange={(event) => update(object.id, { name: event.target.value })} /></span>
          <span>{formatCurrency(object.input.purchasePrice)}</span>
          <span>{view ? formatSignedCurrency(view.wealthDeltaAtHorizon) : '…'}</span>
          <span>{view ? formatOptionalCurrency(view.maximumLeveragedPrice) : '…'}</span>
          <span>{view ? formatCurrency(view.remainingDebtYear10) : '…'}</span>
          <span>{view ? <span className={`decision ${view.decision}`}>{view.decision}</span> : '…'}</span>
          <span className="matrix-row-actions"><button type="button" onClick={() => update(object.id, { input: { ...householdInput, renovationPhases: householdInput.renovationPhases.map((phase) => ({ ...phase })), pvPhases: householdInput.pvPhases.map((phase) => ({ ...phase })) } })}>Replace</button><button type="button" onClick={() => onChange(objects.filter((candidate) => candidate.id !== object.id))}>Remove</button></span>
        </div>;
      })}
    </div> : null}
  </details>;
}

function Fieldset({
  title,
  fields,
  input,
  onChange,
  collapsed = false
}: {
  title: string;
  fields: Field[];
  input: DomainInput;
  onChange: (key: NumericDomainInputKey, value: string) => void;
  collapsed?: boolean;
}) {
  return (
    <details className="field-group" open={!collapsed}>
      <summary>{title}<span>{fields.length} values</span></summary>
      <div className="field-grid">
        {fields.map((field) => (
          <NumberField field={field} input={input} key={field.key} onChange={onChange} />
        ))}
      </div>
    </details>
  );
}

function AdvancedFinancing({
  input,
  onChange,
  onFinancingRatioChange,
  onFinancingModeChange,
  onFinancingDefinitionChange
}: {
  input: DomainInput;
  onChange: (key: NumericDomainInputKey, value: string) => void;
  onFinancingRatioChange: (value: string) => void;
  onFinancingModeChange: (useRatio: boolean) => void;
  onFinancingDefinitionChange: (value: DomainInput['financingRatioDefinition']) => void;
}) {
  const useFinancingRatio = input.financingRatio !== null;

  return (
    <details className="field-group">
      <summary>Advanced leverage<span>optional</span></summary>
      <div className="field-grid">
        <label className="field">
          <span>Financing mode</span>
          <select
            aria-label="Financing mode"
            onChange={(event) => onFinancingModeChange(event.target.value === 'ratio')}
            value={useFinancingRatio ? 'ratio' : 'absolute'}
          >
            <option value="absolute">Derived from equity</option>
            <option value="ratio">Fixed financing ratio</option>
          </select>
        </label>
        <label className="field">
          <span>Financing ratio</span>
          <div className="input-wrap">
            <input
              aria-label="Financing ratio"
              disabled={!useFinancingRatio}
              min="0"
              onChange={(event) => onFinancingRatioChange(event.target.value)}
              step="0.1"
              type="number"
              value={input.financingRatio ?? ''}
            />
            <span className="affix suffix">%</span>
          </div>
        </label>
        <label className="field">
          <span>Ratio basis</span>
          <select
            aria-label="Financing ratio basis"
            disabled={!useFinancingRatio}
            onChange={(event) => onFinancingDefinitionChange(event.target.value as DomainInput['financingRatioDefinition'])}
            value={input.financingRatioDefinition}
          >
            <option value="purchase_price_ltv">Purchase price LTV</option>
            <option value="all_in_financing_ratio">All-in financing ratio</option>
          </select>
        </label>
        <NumberField field={{ key: 'existingDepotMarketValue', label: 'Existing ETF depot', prefix: '€', min: 0, step: 1000 }} input={input} onChange={onChange} />
        <NumberField field={{ key: 'existingDepotUnrealizedGain', label: 'ETF depot unrealized gain', prefix: '€', min: 0, step: 1000 }} input={input} onChange={onChange} />
        <NumberField field={{ key: 'existingDepotWithdrawalForPurchase', label: 'ETF depot sale for purchase', prefix: '€', min: 0, step: 1000 }} input={input} onChange={onChange} />
      </div>
    </details>
  );
}

function NumberField({
  field,
  input,
  onChange
}: {
  field: Field;
  input: DomainInput;
  onChange: (key: NumericDomainInputKey, value: string) => void;
}) {
  return (
    <label className="field">
      <span>{field.label}</span>
      <div className="input-wrap">
        {field.prefix ? <span className="affix">{field.prefix}</span> : null}
        <input
          min={field.min}
          onChange={(event) => onChange(field.key, event.target.value)}
          step={field.step}
          type="number"
          value={input[field.key]}
        />
        {field.suffix ? <span className="affix suffix">{field.suffix}</span> : null}
      </div>
    </label>
  );
}

function ScenarioTable({ result }: { result: DomainOutput }) {
  return (
    <div className="scenario-table" role="table" aria-label="Scenario results">
      <div className="scenario-row header" role="row">
        <span>View</span>
        <span>Intrinsic</span>
        <span>Max</span>
        <span>OWI</span>
        <span>SI</span>
        <span>HV</span>
        <span>Decision</span>
      </div>
      {result.views.map((view) => (
        <ScenarioRow key={view.key} view={view} />
      ))}
    </div>
  );
}

function ScenarioRow({ view }: { view: ViewResult }) {
  return (
    <div className="scenario-row" role="row">
      <strong>{view.key}</strong>
      <span>{formatCurrency(view.intrinsicValue)}</span>
      <span>{formatOptionalCurrency(view.maximumLeveragedPrice)}</span>
      <span>{formatOptionalNumber(view.objectValueIndex)}</span>
      <span>{formatOptionalNumber(view.strategyIndex)}</span>
      <span className={toneForLeverage(view.leverageConsumption)}>
        {formatNullablePercent(view.leverageConsumption)}
      </span>
      <span className={`decision ${view.decision}`}>{view.decision}</span>
    </div>
  );
}

function WealthDeltaTable({ result }: { result: DomainOutput }) {
  const rows = [
    ...result.views.map((view) => ({ key: view.key, context: view.wealthDeltaContext })),
    ...result.rentalViews.map((view) => ({ key: view.key, context: view.wealthDeltaContext })),
    ...result.mixedUseViews.map((view) => ({ key: view.key, context: view.wealthDeltaContext }))
  ];
  return (
    <div className="wealth-delta-table" role="table" aria-label="Wealth delta context">
      <div className="wealth-delta-row header" role="row">
        <span>View</span>
        <span>End delta</span>
        <span>% benchmark</span>
        <span>Today</span>
        <span>€/mo</span>
        <span>€/mo today</span>
      </div>
      {rows.map(({ key, context }) => (
        <div className="wealth-delta-row" key={key} role="row">
          <strong>{key}</strong>
          <span>{formatSignedCurrency(context.nominalWealthDeltaAtHorizon)}</span>
          <span>{formatSignedPercent(context.percentOfBenchmarkTotal)}</span>
          <span>{formatSignedCurrency(context.realWealthDeltaInTodayEur)}</span>
          <span>{formatSignedCurrency(context.monthlyEquivalentNominal)}</span>
          <span>{formatSignedCurrency(context.monthlyEquivalentInTodayEur)}</span>
        </div>
      ))}
    </div>
  );
}

const asymptoticComponentRows: Array<{
  key: keyof Pick<
    ViewResult,
    | 'mortgagePaymentDragPv'
    | 'ownerCostDragPv'
    | 'maintenanceAndCapexDragPv'
    | 'energyBenefitPv'
    | 'avoidedRentBenefitPv'
    | 'elwNetBenefitPv'
    | 'pvElectricityBenefitPv'
    | 'taxEffectPv'
    | 'postPayoffHousingBenefitPv'
  >;
  label: string;
}> = [
  { key: 'mortgagePaymentDragPv', label: 'Kreditraten' },
  { key: 'ownerCostDragPv', label: 'Eigentümerkosten' },
  { key: 'maintenanceAndCapexDragPv', label: 'Instandhaltung und Capex' },
  { key: 'energyBenefitPv', label: 'Energievorteil' },
  { key: 'avoidedRentBenefitPv', label: 'Vermiedene Miete' },
  { key: 'elwNetBenefitPv', label: 'ELW-Nettozufluss' },
  { key: 'pvElectricityBenefitPv', label: 'PV-Stromvorteil' },
  { key: 'taxEffectPv', label: 'Steuereffekt' },
  { key: 'postPayoffHousingBenefitPv', label: 'Wohnvorteil nach Tilgung' }
];

function AsymptoticFamilyWealthTable({ result }: { result: DomainOutput }) {
  const views = ['SQ-ETF', 'ZM-ETF'].map(
    (key) => result.views.find((view) => view.key === key) ?? null
  );

  if (views.every((view) => view === null)) {
    return null;
  }

  return (
    <section className="asymptotic-section" aria-labelledby="asymptotic-heading">
      <div className="comparison-heading">
        <h3 id="asymptotic-heading">Langfristig entgangenes Familienvermögen - Grenzwert</h3>
        <span>gegen Miet-plus-ETF</span>
      </div>
      <div className="asymptotic-table" role="table" aria-label="Asymptotischer Familienvermögensverlust">
        <AsymptoticRow label="Kennzahl" values={['SQ-ETF', 'ZM-ETF']} header />
        <AsymptoticRow
          label="Sofort aus Finanzvermögen entnommen"
          values={views.map((view) => formatViewCurrency(view, 'initialFinancialCapitalDiversion'))}
        />
        <AsymptoticRow
          label="Barwert späterer Cashflow-Nachteile"
          values={views.map((view) => formatAsymptoticComponentTotal(view, 'positive'))}
        />
        <AsymptoticRow
          label="Barwert späterer Cashflow-Vorteile"
          values={views.map((view) => formatAsymptoticComponentTotal(view, 'negative'))}
        />
        <AsymptoticRow
          label="Netto-Barwert späterer Cashflows"
          values={views.map((view) => formatViewOptionalSignedCurrency(view, 'ongoingCashflowDragPresentValue'))}
        />
        <AsymptoticRow
          label="Immobilien-Eigenkapital im Grenzwert"
          values={views.map((view) => formatViewOptionalSignedCurrency(view, 'asymptoticPropertyEquityContribution', -1))}
        />
        <AsymptoticRow
          label="Kapitalisierte ETF-Referenzbasis"
          values={views.map((view) => formatViewOptionalCurrency(view, 'asymptoticBenchmarkCapitalAtPurchase'))}
        />
        <AsymptoticRow
          label="Verlust am gewählten Horizont"
          values={views.map((view) => formatViewRate(view, 'finiteFamilyWealthLossRate'))}
        />
        <AsymptoticRow
          label="Langfristig entgangenes Familienvermögen"
          values={views.map((view) => formatViewRate(view, 'asymptoticFamilyWealthLossRate'))}
          strong
        />
        <AsymptoticRow
          label="Langfristig erhaltenes Familienvermögen"
          values={views.map((view) => formatViewRate(view, 'asymptoticFamilyWealthRetentionRate'))}
        />
        <AsymptoticRow
          label="95-%-Konvergenz erreicht nach"
          values={views.map((view) => formatConvergenceYears(view?.yearsTo95PercentConvergence ?? null))}
        />
        <AsymptoticRow
          label="Status"
          values={views.map((view) => formatAsymptoticStatus(view))}
        />
      </div>
      <details className="asymptotic-components">
        <summary>Barwert-Komponenten</summary>
        <div className="asymptotic-table" role="table" aria-label="Barwert-Komponenten des Grenzwerts">
          <AsymptoticRow label="Komponente" values={['SQ-ETF', 'ZM-ETF']} header />
          {asymptoticComponentRows.map(({ key, label }) => (
            <AsymptoticRow
              key={key}
              label={label}
              values={views.map((view) => formatViewOptionalSignedCurrency(view, key))}
            />
          ))}
        </div>
      </details>
      <p className="asymptotic-disclaimer">
        Die Kennzahl beschreibt den langfristig entgangenen Anteil des gesamten Familienvermögens
        gegenüber dem Referenzpfad. Sie beschreibt nicht den zukünftigen Immobilienanteil am Vermögen.
      </p>
    </section>
  );
}

function AsymptoticRow({
  label,
  values,
  header = false,
  strong = false
}: {
  label: string;
  values: string[];
  header?: boolean;
  strong?: boolean;
}) {
  return (
    <div className={`asymptotic-row${header ? ' header' : ''}${strong ? ' strong' : ''}`} role="row">
      <span>{label}</span>
      {values.map((value, index) => <span key={`${index}-${value}`}>{value}</span>)}
    </div>
  );
}

type AsymptoticCurrencyKey =
  | 'initialFinancialCapitalDiversion'
  | 'ongoingCashflowDragPresentValue'
  | 'asymptoticPropertyEquityContribution'
  | 'asymptoticBenchmarkCapitalAtPurchase'
  | typeof asymptoticComponentRows[number]['key'];

function formatViewCurrency(view: ViewResult | null, key: 'initialFinancialCapitalDiversion'): string {
  return view === null ? 'n/a' : formatCurrency(view[key]);
}

function formatViewOptionalCurrency(
  view: ViewResult | null,
  key: Extract<AsymptoticCurrencyKey, 'asymptoticBenchmarkCapitalAtPurchase'>
): string {
  return view === null ? 'n/a' : formatOptionalCurrency(view[key]);
}

function formatViewOptionalSignedCurrency(
  view: ViewResult | null,
  key: Exclude<AsymptoticCurrencyKey, 'initialFinancialCapitalDiversion' | 'asymptoticBenchmarkCapitalAtPurchase'>,
  multiplier = 1
): string {
  if (view === null || view[key] === null) {
    return 'n/a';
  }
  return formatSignedCurrency(view[key] * multiplier);
}

function formatAsymptoticComponentTotal(
  view: ViewResult | null,
  sign: 'positive' | 'negative'
): string {
  if (view === null) {
    return 'n/a';
  }
  const values = asymptoticComponentRows
    .map(({ key }) => view[key])
    .filter((value): value is number => value !== null);
  if (values.length === 0) {
    return 'n/a';
  }
  const total = values.reduce(
    (sum, value) => sum + (sign === 'positive' ? Math.max(0, value) : Math.min(0, value)),
    0
  );
  return formatSignedCurrency(total);
}

function formatViewRate(
  view: ViewResult | null,
  key: 'finiteFamilyWealthLossRate' | 'asymptoticFamilyWealthLossRate' | 'asymptoticFamilyWealthRetentionRate'
): string {
  const value = view?.[key] ?? null;
  return value === null ? 'n/a' : formatSignedPercent(value * 100);
}

function formatConvergenceYears(value: number | null): string {
  return value === null ? 'nicht erreicht' : `${value} Jahre`;
}

function formatAsymptoticStatus(view: ViewResult | null): string {
  if (view === null) {
    return 'nicht verfügbar';
  }
  const labels: Record<ViewResult['asymptoticLimitStatus'], string> = {
    defined: 'definiert',
    defined_non_robust: 'definiert, nicht robust',
    property_matches_benchmark: 'Immobilie wächst wie Benchmark',
    property_outgrows_benchmark: 'Immobilie wächst schneller',
    cashflow_limit_undefined: 'Cashflow-Grenzwert undefiniert',
    loan_not_finitely_amortized: 'Darlehen nicht vollständig tilgbar',
    invalid_reference_base: 'Referenzbasis ungültig',
    infeasible_strategy: 'Finanzierung nicht durchführbar',
    not_applicable: 'nicht anwendbar'
  };
  return labels[view.asymptoticLimitStatus];
}

function RepaymentComparisonPanel({
  comparison
}: {
  comparison: DomainOutput['repaymentComparison'];
}) {
  const strategies = [comparison.baseStrategy, comparison.comparisonStrategy];
  return (
    <section className="repayment-comparison" aria-label="Repayment comparison">
      <div className="comparison-heading">
        <h3>Repayment comparison</h3>
        <span>{comparison.cashFlowPolicy === 'equal_monthly_budget' ? 'Equal monthly budget' : comparison.cashFlowPolicy}</span>
      </div>
      <div className="repayment-strategies">
        {strategies.map((strategy) => (
          <div className="repayment-strategy" key={strategy.initialRepaymentRate}>
            <strong>{strategy.initialRepaymentRate.toFixed(2)}% repayment</strong>
            <span>Rate {formatCurrency(strategy.initialContractualPayment)}</span>
            <span>Paid off {formatPayoffMonth(strategy.payoffMonth)}</span>
            <span>Interest {formatCurrency(strategy.totalInterest)}</span>
            <span>ETF {formatCurrency(strategy.investmentAccountAtHorizon)}</span>
            <span>Net wealth {formatCurrency(strategy.netWealthAtHorizon)}</span>
          </div>
        ))}
      </div>
      <div className="detail-list comparison-bridge">
        <Detail label="Cash reserve target" value={formatCurrency(comparison.cashReserveTarget)} />
        <Detail label="ETF difference" value={formatSignedCurrency(comparison.investmentAccountDifference)} />
        <Detail label="Cash difference" value={formatSignedCurrency(comparison.cashReserveDifference)} />
        <Detail label="Property equity difference" value={formatSignedCurrency(comparison.terminalPropertyEquityDifference)} />
        <Detail label="Additional interest" value={formatSignedCurrency(comparison.additionalInterest)} />
        <Detail label="Net wealth context" value={formatWealthDeltaCompact(comparison.netWealthDifferenceContext)} strong />
        <Detail label="Break-even ETF return" value={formatNullablePercent(comparison.breakEvenEtfReturnRate)} strong />
      </div>
    </section>
  );
}

function Metric({
  label,
  value,
  suffix,
  tone = 'neutral'
}: {
  label: string;
  value: string;
  suffix?: string;
  tone?: 'neutral' | 'positive' | 'negative';
}) {
  return (
    <div className={`metric ${tone}`}>
      <span>{label}</span>
      <strong>
        {value}
        {suffix ? <small>{suffix}</small> : null}
      </strong>
    </div>
  );
}

function Detail({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={strong ? 'detail strong' : 'detail'}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value);
}

function formatOptionalCurrency(value: number | null): string {
  return value === null ? 'n/a' : formatCurrency(value);
}

function formatSignedCurrency(value: number): string {
  return `${value > 0 ? '+' : value < 0 ? '-' : ''}${formatCurrency(Math.abs(value))}`;
}

function formatSignedPercent(value: number): string {
  return `${value > 0 ? '+' : value < 0 ? '-' : ''}${Math.abs(value).toFixed(1)}%`;
}

function formatWealthDeltaCompact(context: WealthDeltaContext): string {
  return `${formatSignedCurrency(context.nominalWealthDeltaAtHorizon)} · ${formatSignedPercent(context.percentOfBenchmarkTotal)} · ${formatSignedCurrency(context.monthlyEquivalentInTodayEur)}/mo today`;
}

function formatFinancingBasis(view: ViewResult): string {
  if (view.requestedFinancingRatio === null) {
    return 'absolute loan/equity';
  }
  const label = view.financingRatioDefinition === 'purchase_price_ltv' ? 'purchase-price LTV' : 'all-in ratio';
  return `${view.requestedFinancingRatio.toFixed(1)}% ${label}`;
}

function formatPayoffMonth(value: number | null): string {
  if (value === null) {
    return 'after horizon';
  }
  const years = Math.floor(value / 12);
  const months = value % 12;
  return `${years}y ${months}m`;
}

function formatNullablePercent(value: number | null): string {
  return value === null ? 'n/a' : `${value.toFixed(1)}%`;
}

function formatOptionalNumber(value: number | null): string {
  return value === null ? 'n/a' : value.toFixed(0);
}

function toneForLeverage(value: number | null): 'neutral' | 'positive' | 'negative' {
  if (value === null) {
    return 'neutral';
  }
  return value <= 70 ? 'positive' : value > 100 ? 'negative' : 'neutral';
}

function CalculatorPromptPreview({ user, input }: { user: User | null; input: DomainInput }) {
  const [prompt, setPrompt] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const preview = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const payload = await authFetch(user, '/api/account/calculator-prompt/preview', {
        method: 'POST',
        body: JSON.stringify({ domainInput: input })
      }) as { prompt?: unknown; warnings?: unknown };
      setPrompt(typeof payload.prompt === 'string' ? payload.prompt : 'Der Server hat keinen Prompt zurückgegeben.');
      setWarnings(Array.isArray(payload.warnings) ? payload.warnings.filter((value): value is string => typeof value === 'string') : []);
      setError(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Prompt-Vorschau konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };
  const copy = async () => {
    if (!prompt) return;
    try { await navigator.clipboard.writeText(prompt); } catch { setError('Zwischenablage ist nicht verfügbar.'); }
  };

  return <details className="field-group prompt-preview">
    <summary>Bewertungsauftrag / Prompt-Vorschau<span>serverseitig</span></summary>
    <p className="phase-intro">Der Server baut den Prompt aus den aktuellen Rechnerwerten und dem Haushaltsprofil. Diese Vorschau ist ein Entwurf und enthält ausdrücklich keine nutzbare <code>startId</code>.</p>
    {!user ? <p className="loading-text">Für die Prompt-Vorschau bitte anmelden.</p> : <>
      <div className="prompt-actions"><button type="button" disabled={loading} onClick={() => void preview()}>{loading ? 'Erstelle Vorschau…' : 'Vorschau erzeugen'}</button>{prompt ? <button className="secondary-action" type="button" onClick={() => void copy()}>Prompt kopieren</button> : null}</div>
      {error ? <p className="error-text">{error}</p> : null}
      {warnings.length ? <ul className="prompt-warnings">{warnings.map((warning, index) => <li key={`${index}-${warning}`}>{warning}</li>)}</ul> : null}
      {prompt ? <pre className="calculator-prompt">{prompt}</pre> : null}
    </>}
  </details>;
}
